'use client';

import { type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import { useTealium } from '@fshr/context/tealium-context';
import { useTranslations } from '@fshr/context/i18n-context';
import useJumpLinkNav from './use-jump-link-nav';
import BevSafeText from '../bev-safe-text';
import { IconChevronLightLeft, IconChevronLightRight } from '../icon';
import type { JumpLinkStickyTabProps, JumpLinkSection } from './types';

export type { JumpLinkStickyTabProps, JumpLinkSection };

// Mirrors the legacy `data-track-interaction_name` — every jump-link tab reports
// under the same interaction name, with the anchor id as the detail. The legacy
// component fired `Tealium.trackInteraction(<type>, 'day_tab', <anchorId>)`; the
// `useTealium` hook exposes the same call (and no-ops when no provider is present).
const INTERACTION_NAME = 'day_tab';

export default function JumpLinkStickyTab({
	sections,
	className,
	onSectionClick,
	interactionType,
}: JumpLinkStickyTabProps): ReactElement {
	const {
		activeSectionKey,
		canScrollLeft,
		canScrollRight,
		isSticky,
		isVisible,
		navRef,
		pageHeaderBottom,
		placeholderRef,
		scrollTabs,
		scrollTo,
		tabListRef,
	} = useJumpLinkNav({ sections });
	const { trackInteraction } = useTealium();
	const t = useTranslations();

	const trackingEnabled = Boolean(interactionType);

	const handleTabClick = (section: JumpLinkSection, event: React.MouseEvent<HTMLAnchorElement>) => {
		onSectionClick?.(section);
		scrollTo(section, event);
		if (interactionType) trackInteraction(interactionType, INTERACTION_NAME, section.id);
	};

	return (
		<div ref={placeholderRef} className="h-[42px] w-full lg:h-[52px]">
			<nav
				ref={navRef}
				data-jump-link-sticky-tab
				aria-label={t('Page sections')}
				style={isSticky ? { top: pageHeaderBottom } : undefined}
				className={cn(
					'z-20 h-[42px] w-full border-y border-button-primary-inactive-hover bg-surface-default transition-opacity duration-300 lg:h-[52px]',
					className,
					isSticky ? 'fixed inset-x-0' : 'relative',
					!isVisible && 'pointer-events-none invisible opacity-0'
				)}
			>
				<button
					type="button"
					aria-label={t('Scroll sections left')}
					tabIndex={-1}
					onClick={() => scrollTabs('left')}
					className={cn(
						'absolute inset-y-0 left-150 z-10 flex w-400 items-center justify-center bg-surface-default transition-opacity md:left-200 lg:left-500 xl:left-800',
						!canScrollLeft && 'pointer-events-none opacity-0'
					)}
				>
					<IconChevronLightLeft width={12} height={12} />
				</button>

				<div className="spacing-wide-grid flex h-full">
					<ul
						ref={tabListRef}
						className="flex h-full w-full flex-row flex-nowrap gap-300 overflow-x-auto no-scrollbar snap-x lg:gap-400"
					>
						{sections.map(section => (
							<li key={section.key} className="flex h-full snap-center items-center">
								<a
									href={`#${section.id}`}
									data-section-key={section.key}
									onClick={e => handleTabClick(section, e)}
									aria-current={activeSectionKey === section.key ? 'location' : undefined}
									{...(trackingEnabled
										? {
												'data-event-name': 'interaction',
												'data-track-interaction_type': interactionType,
												'data-track-interaction_name': INTERACTION_NAME,
												'data-track-interaction_detail': section.id,
											}
										: {})}
									className={
										'text-subtitle2 ' +
										cn(
											'group relative flex h-full items-center px-020 whitespace-nowrap no-underline transition-colors focus-visible:outline-none',
											activeSectionKey === section.key ? 'text-text-primary' : 'text-text-disabled'
										)
									}
								>
									<span className="translate-y-020">
										<BevSafeText>{section.label}</BevSafeText>
									</span>
									<span
										aria-hidden="true"
										className={cn(
											'absolute bottom-0 inset-x-0 h-020 transition-colors',
											activeSectionKey === section.key ? 'bg-text-primary' : 'bg-transparent'
										)}
									/>
									<span
										aria-hidden="true"
										className="pointer-events-none absolute inset-0 border-2 border-transparent transition-colors group-focus-visible:border-text-primary"
									/>
								</a>
							</li>
						))}
					</ul>
				</div>

				<button
					type="button"
					aria-label={t('Scroll sections right')}
					tabIndex={-1}
					onClick={() => scrollTabs('right')}
					className={cn(
						'absolute inset-y-0 right-150 z-10 flex w-400 items-center justify-center bg-surface-default transition-opacity md:right-200 lg:right-500 xl:right-800',
						!canScrollRight && 'pointer-events-none opacity-0'
					)}
				>
					<IconChevronLightRight width={12} height={12} />
				</button>
			</nav>
		</div>
	);
}
